import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@yp/shared';
import {
  WidgetApiError,
  bookingKey,
  widgetApi,
  type Availability,
  type Booking,
  type Slot,
  type VenueDetail,
  type VenueSummary,
} from './lib/api.js';
import { Footer } from './components/Footer.js';
import { readParams, sourceDetail, type WidgetParams } from './lib/params.js';
import {
  addServiceDays,
  currentServiceDate,
  formatDate,
  formatHijri,
  formatTime,
} from './lib/time.js';
import { PartySizeStep } from './steps/PartySizeStep.js';
import { DateStep } from './steps/DateStep.js';
import { SlotStep } from './steps/SlotStep.js';
import { DetailsStep } from './steps/DetailsStep.js';
import { ConfirmStep } from './steps/ConfirmStep.js';
import { StepRail } from './components/StepRail.js';

export type StepId = 'party' | 'date' | 'slot' | 'details' | 'done';

const STEPS: StepId[] = ['party', 'date', 'slot', 'details'];

/**
 * The booking flow.
 *
 * Party size → date → time → details → confirm, in that order, because each
 * step narrows the next: availability depends on party size, and slots depend
 * on the date. Asking for a name first — as most restaurant widgets do — means
 * collecting personal details from someone who is about to be told there is no
 * table.
 *
 * State lives here rather than in a router. The flow is four steps on one
 * screen; a route per step would add history entries a guest has to back
 * through and would break inside an iframe, where the host page owns the URL.
 */
export function App({ params }: { params: WidgetParams }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;

  const [venue, setVenue] = useState<VenueDetail | null>(null);
  const [venueError, setVenueError] = useState<string | null>(null);
  // Only populated when the widget is opened with no venue in the URL.
  const [venueChoices, setVenueChoices] = useState<VenueSummary[] | null>(null);
  const [chosenSlug, setChosenSlug] = useState<string | null>(null);

  const [step, setStep] = useState<StepId>('party');
  const [partySize, setPartySize] = useState(params.partySize ?? 2);
  const [breakdown, setBreakdown] = useState({ kids02: 0, kids36: 0, kids712: 0 });
  const [serviceDate, setServiceDate] = useState<string>(params.date ?? '');
  const [slot, setSlot] = useState<Slot | null>(null);

  const [availability, setAvailability] = useState<Availability | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [booking, setBooking] = useState<Booking | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ booking_ref: string; starts_at: string } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  // --- venue ---------------------------------------------------------------

  const activeSlug = params.venueSlug ?? chosenSlug;

  /**
   * With no slug in the URL the guest typed the bare domain, which is a normal
   * thing to do with a short printed address. Rather than telling them they
   * failed to specify something, ask which restaurant — and skip the question
   * entirely when there is only one to pick.
   */
  useEffect(() => {
    if (activeSlug) return;
    widgetApi
      .venues()
      .then(({ venues }) => {
        if (venues.length === 0) {
          setVenueError(t('widget.noVenue'));
        } else if (venues.length === 1) {
          setChosenSlug(venues[0]!.slug);
        } else {
          setVenueChoices(venues);
        }
      })
      .catch(() => setVenueError(t('widget.venueUnavailable')));
  }, [activeSlug, t]);

  useEffect(() => {
    if (!activeSlug) return;
    widgetApi
      .venue(activeSlug)
      .then((detail) => {
        setVenue(detail);
        setServiceDate((current) => current || currentServiceDate(venueTime(detail)));
      })
      .catch(() => setVenueError(t('widget.venueUnavailable')));
  }, [activeSlug, t]);

  // --- availability --------------------------------------------------------

  useEffect(() => {
    if (!venue || !serviceDate) return;
    let cancelled = false;

    setLoadingSlots(true);
    widgetApi
      .availability(venue.slug, serviceDate, partySize, locale)
      .then((result) => {
        if (cancelled) return;
        setAvailability(result);
        // A slot chosen for a different date or party size is no longer valid,
        // and silently keeping it would book the wrong thing.
        setSlot(null);
      })
      .catch(() => {
        if (!cancelled) setAvailability(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [venue, serviceDate, partySize, locale]);

  const dates = useMemo(() => {
    if (!venue) return [];
    const first = currentServiceDate(venueTime(venue));
    return Array.from({ length: 14 }, (_, index) => addServiceDays(first, index));
  }, [venue]);

  const submit = useCallback(
    async (guest: {
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      occasion: string;
      requests: string;
      preferredTable: string;
      seatingPreference: string;
      heardAboutUs: string;
      companyName: string;
      marketingOptIn: boolean;
    }) => {
      if (!venue || !slot) return;
      setSubmitting(true);
      setSubmitError(null);

      try {
        const created = await widgetApi.createBooking(
          {
            venue_slug: venue.slug,
            service_date: serviceDate,
            starts_at: slot.starts_at,
            party_size: partySize,
            first_name: guest.firstName,
            ...(guest.lastName ? { last_name: guest.lastName } : {}),
            phone: guest.phone,
            ...(guest.email ? { email: guest.email } : {}),
            locale,
            ...(guest.occasion ? { occasion: guest.occasion } : {}),
            ...(guest.requests ? { special_requests: guest.requests } : {}),
            ...(guest.preferredTable ? { preferred_table: guest.preferredTable } : {}),
            ...(guest.seatingPreference ? { seating_preference: guest.seatingPreference } : {}),
            ...(guest.heardAboutUs ? { heard_about_us: guest.heardAboutUs } : {}),
            ...(guest.companyName
              ? { is_corporate: true, company_name: guest.companyName }
              : {}),
            // Sent whenever any child band is filled in; the server treats an
            // absent breakdown as "all adults".
            ...(breakdown.kids02 || breakdown.kids36 || breakdown.kids712
              ? {
                  kids_0_2: breakdown.kids02,
                  kids_3_6: breakdown.kids36,
                  kids_7_12: breakdown.kids712,
                }
              : {}),
            marketing_opt_in: guest.marketingOptIn,
            source_detail: sourceDetail(params),
          },
          bookingKey({
            venueSlug: venue.slug,
            startsAt: slot.starts_at,
            partySize,
            phone: guest.phone,
          }),
        );

        setBooking(created);
        setStep('done');
      } catch (err) {
        // The slot going while a guest typed their name is the common failure,
        // and it needs its own message plus a way back to the times.
        // Checked BEFORE isSlotGone: both are 409s, and telling a guest who
        // already has a booking that their table was taken sends them back to
        // pick another time they will never be allowed to book.
        if (err instanceof WidgetApiError && err.isAlreadyBooked) {
          setDuplicate(err.existingBooking);
        } else if (err instanceof WidgetApiError && err.isSlotGone) {
          setSubmitError(t('widget.slotGone'));
          setStep('slot');
          setSlot(null);
        } else if (err instanceof WidgetApiError && err.isRateLimited) {
          setSubmitError(t('errors.rateLimited'));
        } else {
          setSubmitError(t('errors.generic'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [venue, slot, serviceDate, partySize, breakdown, locale, params, t],
  );

  if (venueError) {
    return (
      <Shell title={t('app.name')} locale={locale} venue={null}>
        <p className="text-center text-sm text-brand-ink-soft">{venueError}</p>
      </Shell>
    );
  }

  if (!venue && venueChoices) {
    return (
      <Shell title={t('app.name')} locale={locale} venue={null}>
        <h2 className="font-display text-xl text-brand-ink">{t('widget.chooseVenue')}</h2>
        <p className="mt-1 text-sm text-brand-ink-soft">{t('widget.chooseVenueHint')}</p>
        <ul className="mt-5 space-y-3">
          {venueChoices.map((choice) => (
            <li key={choice.slug}>
              <button
                type="button"
                onClick={() => setChosenSlug(choice.slug)}
                className="w-full rounded-xl border border-brand-gold/60 bg-white p-4 text-start shadow-sm transition hover:border-brand-bronze hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-bronze"
              >
                <span className="block font-display text-lg text-brand-ink">
                  {choice.name[locale]}
                </span>
                {choice.address[locale] ? (
                  <span className="mt-0.5 block text-sm text-brand-ink-soft">
                    {choice.address[locale]}
                  </span>
                ) : null}
                {/* On the chooser, not only after choosing: a guest deciding
                    between two branches needs to know one is closed BEFORE
                    picking it and filling in a form. */}
                {choice.notice ? (
                  <span className="mt-2 block rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-700">
                    {choice.notice[locale]}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </Shell>
    );
  }

  if (!venue) {
    return (
      <Shell title={t('app.name')} locale={locale} venue={null}>
        <p className="text-center text-sm text-brand-ink-soft">{t('app.loading')}</p>
      </Shell>
    );
  }

  const venueName = locale === 'ar' ? venue.name.ar : venue.name.en;

  return (
    <div className="min-h-screen">
      {/* A dark band behind the mark, as on the restaurant's own site. It also
          does something practical: it separates the brand from the form, so the
          form can be plain white and stay easy to fill in. */}
      <div className="bg-brand-ink px-5 pb-10 pt-8 text-center">
        <img
          src="/logo.png"
          alt=""
          className="mx-auto h-32 w-auto sm:h-36"
          width={262}
          height={300}
        />
        <h1 className="mt-3 font-display text-2xl text-white">{venueName}</h1>
        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-brand-gold">
          {t('widget.tagline')}
        </p>
      </div>

      <main className="mx-auto -mt-6 flex max-w-md flex-col gap-5 px-5 pb-10">
        <div className="rounded-xl border border-brand-gold/50 bg-white p-5 shadow-sm">
        {venue.notice ? (
          <p
            role="status"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700"
          >
            {venue.notice[locale]}
          </p>
        ) : null}

        {step !== 'done' ? (
          <p className="mb-4 text-center text-xs font-medium text-brand-bronze">
            {formatDate(serviceDate || dates[0]!, venueTime(venue), locale)}
            {' · '}
            {formatHijri(serviceDate || dates[0]!, venueTime(venue), locale)}
          </p>
        ) : null}

        {/* A blocking notice replaces the form rather than sitting above it.
            Leaving four working steps under "closed for refurbishment" invites
            a guest to fill them in and be refused at the last one — and the
            server does refuse, so the only thing on offer would be wasted
            effort. The footer's phone number stays, because that is the real
            answer for someone who still wants a table. */}
        {venue.notice_blocks || step === 'done' ? null : (
          <StepRail steps={STEPS} current={step} onGoTo={(target) => setStep(target)} />
        )}

        {duplicate ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-brand-gold bg-brand-gold-light px-4 py-3 text-sm text-brand-ink"
          >
            <p className="font-medium">{t('widget.alreadyBookedTitle')}</p>
            <p className="mt-1">
              {t('widget.alreadyBookedBody', {
                ref: duplicate.booking_ref,
                time: formatTime(duplicate.starts_at, venueTime(venue), locale),
              })}
            </p>
            <p className="mt-2">
              {t('widget.alreadyBookedCall')}{' '}
              <a
                href="tel:+97444111502"
                dir="ltr"
                className="font-medium text-brand-teal underline underline-offset-2"
              >
                +974 4411 1502
              </a>
            </p>
          </div>
        ) : null}

      {submitError ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {submitError}
        </p>
      ) : null}

      {venue.notice_blocks ? null : step === 'party' ? (
        <PartySizeStep
          value={partySize}
          onChange={setPartySize}
          breakdown={breakdown}
          onBreakdownChange={setBreakdown}
          onNext={() => setStep('date')}
          {...(venueChoices && !params.venueSlug
            ? {
                // Only when they picked a branch here. A guest who arrived on
                // /yasmine-pearl from a QR code has no chooser to go back to,
                // and offering one would send them somewhere they never were.
                onBack: () => {
                  setChosenSlug(null);
                  setVenue(null);
                  setStep('party');
                },
              }
            : {})}
        />
      ) : null}

      {step === 'date' ? (
        <DateStep
          dates={dates}
          value={serviceDate}
          venue={venue}
          locale={locale}
          onChange={setServiceDate}
          onBack={() => setStep('party')}
          onNext={() => setStep('slot')}
        />
      ) : null}

      {step === 'slot' ? (
        <SlotStep
          availability={availability}
          loading={loadingSlots}
          selected={slot}
          venue={venue}
          locale={locale}
          partySize={partySize}
          serviceDate={serviceDate}
          onBack={() => setStep('date')}
          onSelect={(chosen) => {
            setSlot(chosen);
            setStep('details');
          }}
        />
      ) : null}

      {step === 'details' && slot ? (
        <DetailsStep
          venue={venue}
          slot={slot}
          partySize={partySize}
          locale={locale}
          submitting={submitting}
          onBack={() => setStep('slot')}
          onSubmit={submit}
        />
      ) : null}

      {step === 'done' && booking ? (
          <ConfirmStep booking={booking} venue={venue} locale={locale} />
        ) : null}
        </div>

        <Footer venue={venue} locale={locale} />
      </main>
    </div>
  );
}

/**
 * The page: dark brand band, a card, and the footer.
 *
 * Shared by every screen. It exists because the branch chooser did NOT use it —
 * it was an early return with its own bare markup — so a guest arriving at the
 * bare domain got a logo on a blank page with no footer and no way to reach
 * anyone. Which is exactly the screen most likely to need a phone number, since
 * they have not chosen a restaurant yet.
 *
 * `venue` is null before one is chosen; the footer then shows the group's
 * contacts without a branch address, which is the truthful thing to show.
 */
function Shell({
  title,
  locale,
  venue,
  children,
}: {
  title: string;
  locale: Locale;
  venue: VenueDetail | null;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen">
      <div className="bg-brand-ink px-5 pb-10 pt-8 text-center">
        <img
          src="/logo.png"
          alt=""
          className="mx-auto h-32 w-auto sm:h-36"
          width={262}
          height={300}
        />
        <h1 className="mt-3 font-display text-2xl text-white">{title}</h1>
        <p className="mt-1 text-xs uppercase tracking-[0.2em] text-brand-gold">
          {t('widget.tagline')}
        </p>
      </div>

      <main className="mx-auto -mt-6 flex max-w-md flex-col gap-5 px-5 pb-10">
        <div className="rounded-xl border border-brand-gold/50 bg-white p-5 shadow-sm">
          {children}
        </div>
        <Footer venue={venue} locale={locale} />
      </main>
    </div>
  );
}

/** The subset of the venue that the time helpers need. */
function venueTime(venue: VenueDetail) {
  return {
    timezone: venue.timezone,
    // The public endpoint does not expose the cutoff hour, and it does not need
    // to: 6 matches every venue's default, and the SERVER re-derives the real
    // service date on write and rejects a mismatch. The widget's copy only has
    // to be right about which day to show first.
    serviceDayCutoffHour: 6,
    digitStyle: venue.digit_style,
  };
}

export { readParams };
